const { readWithRetry, sdkJsonRequest } = require("./riotRequestRecovery");

const RIOT_LOGIN_URL = "https://auth.riotgames.com/authorize?redirect_uri=https%3A%2F%2Fplayvalorant.com%2Fopt_in&client_id=play-valorant-web-prod&response_type=token%20id_token&nonce=1&scope=account%20openid";
const RIOT_LOGIN_SCRIPT_URL = "https://project-test.gamersky.com/GSAppJSApi/tests/ui.openValorantLoginDialog.js";

function getAccessToken(response) {
    if (!response || typeof response.urlsRequested !== "string") {
        return null;
    }
    // Use the latest actual redirect, not parameter order or a token from an older login.
    for (const value of response.urlsRequested.split(",").reverse()) {
        try {
            const url = new URL(/^https:\/\//i.test(value.trim()) ? value.trim() : decodeURIComponent(value.trim()));
            if (url.protocol !== "https:" || url.hostname !== "playvalorant.com"
                || !/^\/opt_in\/?$/.test(url.pathname) || url.username || url.password || url.port) continue;
            const params = new URLSearchParams(url.hash ? url.hash.slice(1) : url.search.slice(1));
            if (!params.has("access_token") && !params.has("error") && !params.has("id_token")) continue;
            const token = params.get("access_token");
            if (params.has("error") || params.getAll("access_token").length !== 1) return null;
            return typeof token === "string" && /^[A-Za-z0-9._~-]+$/.test(token) ? token : null;
        } catch (error) {
            continue;
        }
    }
    return null;
}

async function loginWithShopRiotDialog(appApi, options = {}) {
    const notify = (stage, detail) => {
        try { if (options.onStage) options.onStage(stage, detail); } catch (error) { /* Diagnostics only. */ }
    };
    if (!appApi || !appApi.UI || typeof appApi.UI.openValorantLoginDialog !== "function"
        || !appApi.Net || typeof appApi.Net.send !== "function") {
        throw new Error("RIOT_LOGIN_UNSUPPORTED");
    }
    const assertCurrent = () => {
        if (options.isCurrent && !options.isCurrent()) {
            throw new Error("RIOT_LOGIN_OBSOLETE");
        }
    };
    assertCurrent();
    const accessToken = await new Promise((resolve, reject) => {
        let settled = false;
        let launchTimer;
        let timeoutTimer;
        const finish = (error, token) => {
            if (settled) return;
            settled = true;
            clearTimeout(launchTimer);
            clearTimeout(timeoutTimer);
            if (error) reject(error);
            else resolve(token);
        };
        timeoutTimer = setTimeout(() => finish(new Error("RIOT_LOGIN_TIMEOUT")), options.loginTimeoutMs || 600000);
        launchTimer = setTimeout(() => {
            try {
                assertCurrent();
                notify("login_window_open");
                appApi.UI.openValorantLoginDialog(
                    RIOT_LOGIN_URL,
                    {},
                    options.scriptUrl || RIOT_LOGIN_SCRIPT_URL,
                    (response) => {
                        if (settled) return;
                        try {
                            assertCurrent();
                            const token = getAccessToken(response);
                            notify("login_callback", {
                                hasUrlsRequested: Boolean(response && typeof response.urlsRequested === "string"),
                                hasAccessToken: Boolean(token),
                            });
                            finish(token ? null : new Error("RIOT_LOGIN_CANCELLED"), token);
                        } catch (error) {
                            finish(error);
                        }
                    }
                );
            } catch (error) {
                finish(error);
            }
        }, options.launchDelayMs === undefined ? 100 : options.launchDelayMs);
    });

    const tokenDeadline = Date.now() + 60000;
    const retryBudget = { remaining: 2 };
    const assertTokenCurrent = () => {
        assertCurrent();
        if (Date.now() >= tokenDeadline) throw Object.assign(new Error("请求超时"), { code: "REQUEST_TIMEOUT" });
    };
    const requestToken = async url => {
        const response = await readWithRetry(() => sdkJsonRequest(appApi, url, "POST", {
            Authorization: "Bearer " + accessToken,
        }, {}, {
            timeout: Math.max(1, Math.min(options.requestTimeoutMs || 15000, tokenDeadline - Date.now())),
            timeoutCode: "REQUEST_TIMEOUT", timeoutMessage: "请求超时",
            isCurrent: options.isCurrent, cancelCode: "RIOT_LOGIN_OBSOLETE",
        }), { check: assertTokenCurrent, deadline: tokenDeadline, budget: retryBudget, retryDelayMs: options.retryDelayMs });
        return response.data;
    };
    assertCurrent();
    notify("entitlements_started");
    const entitlements = await requestToken("https://entitlements.auth.riotgames.com/api/token/v1");
    assertCurrent();
    notify("entitlements_completed", {
        valueType: entitlements === null ? "null" : typeof entitlements,
        hasEntitlementsToken: Boolean(entitlements && typeof entitlements.entitlements_token === "string" && entitlements.entitlements_token.trim()),
    });
    if (!entitlements || typeof entitlements.entitlements_token !== "string" || !entitlements.entitlements_token.trim()) {
        throw new Error("RIOT_SESSION_NOT_READY");
    }
    notify("userinfo_started");
    const user = await requestToken("https://auth.riotgames.com/userinfo");
    assertCurrent();
    notify("userinfo_completed", {
        valueType: user === null ? "null" : typeof user,
        hasPuuid: Boolean(user && typeof user.sub === "string" && user.sub.trim()),
    });
    if (!user || typeof user.sub !== "string" || !user.sub.trim()) {
        throw new Error("RIOT_SESSION_NOT_READY");
    }
    assertCurrent();
    if (options.expectedPuuid && user.sub.trim() !== options.expectedPuuid) {
        throw new Error("RIOT_ACCOUNT_MISMATCH");
    }
    return {
        puuid: user.sub.trim(),
        accessToken,
        entitlementsToken: entitlements.entitlements_token.trim(),
        loginMode: "VShopMode",
        // The official login uses the native WebView cookie jar, exactly like
        // the shop's official-login button. Do not invent an API-mode cookie.
        headersCookie: "",
    };
}

module.exports = { loginWithShopRiotDialog };
